import axios from 'axios'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

// antd
import { Button as ButtonAnt, message, Input, Skeleton, Tooltip } from 'antd'
import { Icon } from '@iconify/react'

import { SearchIconWhite } from '../../assets/icons'

// slices
import { loginState } from '../../modules/login/login.slice'
import { connectJiraOauth } from '../../modules/jira/jira.slice'
import {
    clearGlobalSearch,
    fetchGlobalSearch,
    fetchRecentItems,
    globalSearchState,
} from '../../modules/globalSearch/globalSearch.slice'

// css
import headerStyles from './headerRightSection.module.css'
import { showActionNotification } from '../../components/notification'
import HeaderNotifications from './HeaderNotifications'
import { PRIVATE_ROUTES, buildProjectTaskTabSearch } from '../../routes'
import { getBackendBaseUrl } from '../../helpers/axios'

const PROJECT_TASK_VIEW_SEGMENTS = new Set(['board', 'list', 'roadmap'])

const SECTION_PAGE_SIZE = 10

const DEFAULT_SECTION_LIMITS = {
    task: SECTION_PAGE_SIZE,
    supportTask: SECTION_PAGE_SIZE,
    project: SECTION_PAGE_SIZE,
    // assignee: SECTION_PAGE_SIZE,
}

const DEFAULT_SECTION_SKIPS = {
    task: 0,
    supportTask: 0,
    project: 0,
    // assignee: 0,
}

const SearchPaletteInfiniteSentinel = ({
    enabled,
    loading,
    scrollRootRef,
    onLoadMore,
}) => {
    const sentinelRef = useRef(null)

    useEffect(() => {
        if (!enabled || loading) return undefined
        const sentinel = sentinelRef.current
        if (!sentinel) return undefined

        const root = scrollRootRef?.current || null
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    onLoadMore?.()
                }
            },
            {
                root,
                rootMargin: '120px 0px',
                threshold: 0,
            }
        )

        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [enabled, loading, onLoadMore, scrollRootRef])

    if (!enabled) return null

    return (
        <div
            ref={sentinelRef}
            className={headerStyles.searchPaletteInfiniteSentinel}
            aria-hidden={!loading}
        >
            {loading ? (
                <span className={headerStyles.searchPaletteInfiniteSentinelInner}>
                    <Icon icon="line-md:loading-loop" width={14} height={14} />
                    <span>Loading more...</span>
                </span>
            ) : (
                <span className={headerStyles.searchPaletteInfiniteSentinelSpacer} />
            )}
        </div>
    )
}

const pickProjectIdFromTaskSearchItem = (item) => {
    if (!item) return null
    const p = item.projectId
    if (p && typeof p === 'object' && p._id) return String(p._id)
    if (p) return String(p)
    if (item.project?._id) return String(item.project._id)
    return null
}

const resolveTaskDeepLinkView = (pathname, targetProjectId) => {
    if (!targetProjectId) return 'board'
    const match = String(pathname || '').match(
        /^\/dashboard\/projects\/([^/]+)\/(board|list|roadmap)(?:\/[^/]+)?$/
    )
    if (match && match[1] === String(targetProjectId)) {
        const seg = match[2]
        return PROJECT_TASK_VIEW_SEGMENTS.has(seg) ? seg : 'board'
    }
    return 'board'
}

const EllipsisTooltipText = ({
    text,
    className,
    placement = 'topLeft',
}) => {
    const textRef = useRef(null)
    const [tooltipOpen, setTooltipOpen] = useState(false)
    const displayText = text || '-'

    return (
        <Tooltip
            title={displayText}
            placement={placement}
            open={tooltipOpen}
            onOpenChange={(open) => {
                if (!open) {
                    setTooltipOpen(false)
                    return
                }
                const el = textRef.current
                setTooltipOpen(
                    Boolean(el && el.scrollWidth > el.clientWidth + 1)
                )
            }}
        >
            <span ref={textRef} className={className}>
                {displayText}
            </span>
        </Tooltip>
    )
}

/** True for global-search task rows (e.g. projectId string + _id, optional taskKey). */
const isSearchTaskRowItem = (item) => {
    if (!item?._id) return false
    const pid = pickProjectIdFromTaskSearchItem(item)
    if (!pid) return false
    if (item.taskKey != null && String(item.taskKey).trim() !== '') {
        return true
    }
    return String(item._id) !== String(pid)
}

/* const SAVED_SEARCHES = [
    {
        key: 'created',
        label: "Tasks I've created",
        icon: 'mdi:check-circle-outline',
    },
    {
        key: 'assigned',
        label: "Tasks I've assigned to others",
        icon: 'mdi:arrow-right-thin',
    },
    {
        key: 'completed',
        label: 'Recently completed tasks',
        icon: 'mdi:check-circle',
    },
    { key: 'deleted', label: 'Deleted', icon: 'mdi:delete-outline' },
] */

const HeaderRightSection = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()

    const { userProfile } = useSelector(loginState)
    const {
        searchFetchStatus,
        sections: globalSearchSections,
        recentItems,
        recentFetchStatus,
    } = useSelector(globalSearchState)

    const [loadingButton, setLoadingButton] = useState(false)
    const [jiraConnecting, setJiraConnecting] = useState(false)
    const [paletteOpen, setPaletteOpen] = useState(false)
    const [paletteQuery, setPaletteQuery] = useState('')
    const [sectionSkips, setSectionSkips] = useState(DEFAULT_SECTION_SKIPS)
    const [activeTab, setActiveTab] = useState(null)
    const paletteInputRef = useRef(null)
    const searchResultsScrollRef = useRef(null)
    const pendingLoadMoreRef = useRef(null)
    const [loadingMoreSectionKey, setLoadingMoreSectionKey] = useState(null)
    const globalSearchLoading = searchFetchStatus === 'loading'
    const paletteQueryRef = useRef(paletteQuery)
    paletteQueryRef.current = paletteQuery
    const sectionSkipsRef = useRef(sectionSkips)
    sectionSkipsRef.current = sectionSkips
    const sectionKeyByTitle = useMemo(
        () => ({
            'support task': 'supportTask',
            'support tasks': 'supportTask',
            task: 'task',
            tasks: 'task',
            'development task': 'task',
            'development tasks': 'task',
            project: 'project',
            projects: 'project',
            // assignee: 'assignee',
        }),
        []
    )

    const categoryTabs = useMemo(
        () => [
            { key: 'task', label: 'Tasks', icon: 'lucide:list-checks' },
            {
                key: 'supportTask',
                label: 'Support Tasks',
                icon: 'lucide:headset',
            },
            {
                key: 'project',
                label: 'Projects',
                icon: 'lucide:folder-kanban',
            },
            // { key: 'assignee', label: 'People', icon: 'mdi:account-outline' },
        ],
        []
    )
    const categoryTabSectionKeys = useMemo(
        () => ({
            task: ['task'],
            supportTask: ['supportTask'],
            project: ['project'],
            // assignee: ['assignee'],
        }),
        []
    )
    const filteredGlobalSearchSections = useMemo(() => {
        // Hide assignee / People sections from global search results
        const withoutAssignees = (globalSearchSections || []).filter(
            (section) => {
                const title = String(section?.title || '')
                    .toLowerCase()
                    .trim()
                return title !== 'assignee' && title !== 'assignees' && title !== 'people'
            }
        )

        if (!activeTab) return withoutAssignees
        const allowedSectionKeys = categoryTabSectionKeys[activeTab] || []

        return withoutAssignees.filter((section) => {
            const normalizedSectionKey =
                sectionKeyByTitle[String(section?.title || '').toLowerCase()] ||
                section?.key
            return allowedSectionKeys.includes(normalizedSectionKey)
        })
    }, [
        activeTab,
        categoryTabSectionKeys,
        globalSearchSections,
        sectionKeyByTitle,
    ])

    useEffect(() => {
        const onKeyDown = (e) => {
            const key = String(e.key || '').toLowerCase()
            const mod = e.ctrlKey || e.metaKey
            if (mod && key === 'k') {
                e.preventDefault()
                setPaletteOpen(true)
                return
            }
            if (key === 'escape') {
                setPaletteOpen(false)
            }
        }

        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [])

    useEffect(() => {
        if (!paletteOpen) return
        setPaletteQuery('')
        setSectionSkips({ ...DEFAULT_SECTION_SKIPS })
        pendingLoadMoreRef.current = null
        setLoadingMoreSectionKey(null)
        dispatch(clearGlobalSearch())
        setActiveTab(null)
        if (recentFetchStatus === 'idle') {
            dispatch(fetchRecentItems())
        }
        // focus after open paint
        setTimeout(() => {
            paletteInputRef.current?.focus?.()
        }, 0)
    }, [paletteOpen, dispatch, recentFetchStatus])

    // Fresh search (replace): always limit 10 + skip 0 for every section
    useEffect(() => {
        if (!paletteOpen) return

        const searchText = paletteQuery?.trim()
        // If no query and no active tab, keep default state (recents + saved searches).
        if (!searchText && !activeTab) {
            dispatch(clearGlobalSearch())
            return
        }

        setSectionSkips({ ...DEFAULT_SECTION_SKIPS })
        pendingLoadMoreRef.current = null
        setLoadingMoreSectionKey(null)

        const timer = setTimeout(() => {
            dispatch(
                fetchGlobalSearch({
                    search: searchText || '',
                    limit: { ...DEFAULT_SECTION_LIMITS },
                    skip: { ...DEFAULT_SECTION_SKIPS },
                    append: false,
                })
            )
        }, 350)

        return () => {
            clearTimeout(timer)
        }
    }, [paletteOpen, paletteQuery, activeTab, dispatch])

    /** @param {'task' | 'supportTask' | 'unknown'} taskKind */
    const navigateToTaskFromSearch = (item, taskKind = 'unknown') => {
        const projectId = pickProjectIdFromTaskSearchItem(item)
        const taskId = item?._id
        if (!projectId || !taskId) return
        let view = resolveTaskDeepLinkView(location.pathname, projectId)
        if (taskKind === 'task') {
            view = view === 'list' ? 'list' : 'board'
        }
        const isDevelopment = taskKind === 'task'
        const search = isDevelopment
            ? buildProjectTaskTabSearch('development')
            : ''
        navigate({
            pathname: PRIVATE_ROUTES.projects.projectIdViewTask(
                projectId,
                view,
                taskId
            ),
            search,
        })
        setPaletteOpen(false)
    }

    useEffect(() => {
        if (searchFetchStatus === 'error') {
            const failedSectionKey = pendingLoadMoreRef.current
            if (failedSectionKey) {
                setSectionSkips((prev) => ({
                    ...prev,
                    [failedSectionKey]: Math.max(
                        0,
                        (prev?.[failedSectionKey] || 0) - SECTION_PAGE_SIZE
                    ),
                }))
            }
        }
        if (searchFetchStatus === 'loaded' || searchFetchStatus === 'error') {
            pendingLoadMoreRef.current = null
            setLoadingMoreSectionKey(null)
        }
    }, [searchFetchStatus])

    const handleLoadMoreSection = useCallback(
        (sectionTitle) => {
            const sectionKey =
                sectionKeyByTitle[String(sectionTitle || '').toLowerCase()]
            if (!sectionKey) return
            if (globalSearchLoading) return
            if (pendingLoadMoreRef.current === sectionKey) return

            const nextSkip =
                (sectionSkipsRef.current?.[sectionKey] || 0) + SECTION_PAGE_SIZE
            const nextSkips = {
                ...sectionSkipsRef.current,
                [sectionKey]: nextSkip,
            }

            pendingLoadMoreRef.current = sectionKey
            setLoadingMoreSectionKey(sectionKey)
            setSectionSkips(nextSkips)

            dispatch(
                fetchGlobalSearch({
                    search: paletteQueryRef.current?.trim() || '',
                    limit: { ...DEFAULT_SECTION_LIMITS },
                    skip: nextSkips,
                    append: true,
                    appendSectionKey: sectionKey,
                })
            )
        },
        [dispatch, globalSearchLoading, sectionKeyByTitle]
    )

    // SUPER ADMIN LOGIN
    const adminLogin = async () => {
        setLoadingButton(true)
        if (window.location.hostname === 'localhost') {
            try {
                const response = await axios({
                    url: `http://localhost:5000/workspace/user-workspace-login?workSpaceId=${userProfile?.backToAdmin?.workspaceId}&userId=${userProfile?.backToAdmin?.userId}`,
                    method: 'get',
                })
                if (response?.data?.success) {
                    setLoadingButton(false)
                    navigate('/')
                    window.location.reload()
                }
            } catch (error) {
                setLoadingButton(false)
                message.error(error?.response?.data?.message)
            }
        } else {
            const response = await axios({
                url: `https://${userProfile?.currentWorkSpace?.domain}/backend/workspace/user-workspace-login?workSpaceId=${userProfile?.backToAdmin?.workspaceId}&userId=${userProfile?.backToAdmin?.userId}`,
                method: 'get',
            })
            if (response?.data?.success) {
                setLoadingButton(false)
                navigate('/')
                window.location.reload()
            } else {
                setLoadingButton(false)
                message.error(response?.data?.message)
            }
        }
    }

    const getInitials = (name) => {
        if (!name) return '?'
        const parts = String(name).trim().split(/\s+/)
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }

    const avatarPalette = [
        '#0ea5e9',
        '#10b981',
        '#f59e0b',
        '#8b5cf6',
        '#ec4899',
        '#ef4444',
    ]
    const getAvatarBg = (str) => {
        if (!str) return avatarPalette[0]
        let h = 0
        for (let i = 0; i < str.length; i++)
            h = str.charCodeAt(i) + ((h << 5) - h)
        return avatarPalette[Math.abs(h) % avatarPalette.length]
    }

    const renderPaletteRow = (item, type, onClickFn) => {
        const isProject = type === 'project'
        const isPerson = type === 'assignee'
        return (
            <div
                key={item._id}
                className={headerStyles.searchPaletteRow}
                role="button"
                tabIndex={0}
                onClick={onClickFn}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') e.preventDefault()
                }}
            >
                {isProject ? (
                    <div
                        className={headerStyles.searchPaletteRowColorDot}
                        style={{ background: item.colorCode || '#6b7280' }}
                    />
                ) : isPerson ? (
                    item.profilePicture ? (
                        <img
                            src={item.profilePicture}
                            alt={item.name || 'User'}
                            className={headerStyles.searchPaletteAvatar}
                            style={{
                                objectFit: 'cover',
                                border: 'none',
                                flexShrink: 0,
                            }}
                        />
                    ) : (
                        <div
                            className={headerStyles.searchPaletteAvatar}
                            style={{
                                background: getAvatarBg(item.name),
                                color: '#fff',
                                border: 'none',
                                flexShrink: 0,
                            }}
                        >
                            {getInitials(item.name)}
                        </div>
                    )
                ) : (
                    <Icon
                        icon="simple-line-icons:check"
                        width={16}
                        height={16}
                        style={{ color: '#94a3b8', minWidth: '16px' }}
                    />
                )}

                {isPerson ? (
                    <div className={headerStyles.searchPaletteRowPersonLine}>
                        <EllipsisTooltipText
                            text={item.title || item.name}
                            className={headerStyles.searchPaletteRowTitle}
                        />
                        {item.email ? (
                            <EllipsisTooltipText
                                text={item.email}
                                className={headerStyles.searchPaletteRowEmail}
                            />
                        ) : null}
                    </div>
                ) : (
                    <div className={headerStyles.searchPaletteRowText}>
                        <EllipsisTooltipText
                            text={item.title || item.name}
                            className={headerStyles.searchPaletteRowTitle}
                        />
                        {!isProject &&
                            (item.projectName || item.assigneeName) && (
                                <span
                                    className={headerStyles.searchPaletteRowSub}
                                >
                                    <span
                                        className={
                                            headerStyles.searchPaletteRowSubDot
                                        }
                                    />
                                    <EllipsisTooltipText
                                        text={
                                            item.projectName ||
                                            item.assigneeName
                                        }
                                        className={
                                            headerStyles.searchPaletteRowSubText
                                        }
                                    />
                                </span>
                            )}
                    </div>
                )}

                <div className={headerStyles.searchPaletteRowRight}>
                    {isProject ? (
                        <div className={headerStyles.searchPaletteAvatars}>
                            {(item.assignees || item.members || [])
                                .slice(0, 3)
                                .map((a, i) => (
                                    <div
                                        key={i}
                                        className={
                                            headerStyles.searchPaletteAvatar
                                        }
                                        style={{
                                            background:
                                                a.color ||
                                                getAvatarBg(a.name || a),
                                            color: '#fff',
                                            border: '2px solid #fff',
                                            marginLeft: i > 0 ? -8 : 0,
                                            position: 'relative',
                                            zIndex: 3 - i,
                                        }}
                                        title={a.name || a}
                                    >
                                        {getInitials(a.name || a)}
                                    </div>
                                ))}
                            {(item.hasMoreAssignees ||
                                (item.assignees || item.members || []).length >
                                    3) && (
                                <div
                                    className={
                                        headerStyles.searchPaletteMoreBtn
                                    }
                                >
                                    <Icon
                                        icon="mdi:dots-horizontal"
                                        width={14}
                                        height={14}
                                    />
                                </div>
                            )}
                        </div>
                    ) : item.assigneeName ? (
                        item.assigneeProfilePicture ? (
                            <img
                                src={item.assigneeProfilePicture}
                                alt={item.assigneeName || 'Assignee'}
                                className={headerStyles.searchPaletteAvatar}
                                style={{
                                    objectFit: 'cover',
                                    border: 'none',
                                }}
                                title={item.assigneeName}
                            />
                        ) : (
                            <div
                                className={headerStyles.searchPaletteAvatar}
                                style={{
                                    background: getAvatarBg(item.assigneeName),
                                    color: '#fff',
                                    border: 'none',
                                }}
                                title={item.assigneeName}
                            >
                                {getInitials(item.assigneeName)}
                            </div>
                        )
                    ) : null}
                </div>
            </div>
        )
    }

    return (
        <>
            <div className={headerStyles.rightSection}>
                {userProfile?.backToAdmin && (
                    <ButtonAnt
                        style={{
                            height: '48px',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        className={headerStyles.actionButton}
                        loading={loadingButton}
                        disabled={loadingButton}
                        onClick={() => adminLogin()}
                    >
                        Back to SuperAdmin
                    </ButtonAnt>
                )}
                {userProfile &&
                    (userProfile?.isSuperAdmin ||
                        userProfile?.isExistingUser) && (
                        <ButtonAnt
                            className={headerStyles.jiraConnectBtn}
                            loading={jiraConnecting}
                            disabled={
                                jiraConnecting || userProfile?.jiraConnected
                            }
                            onClick={async () => {
                                if (userProfile?.jiraConnected) return
                                setJiraConnecting(true)
                                try {
                                    const result =
                                        await dispatch(connectJiraOauth())
                                    const data = result?.payload?.data || {}
                                    const status = data?.data || {}

                                    if (data?.success && status?.connected) {
                                        showActionNotification({
                                            type: 'success',
                                            message: 'Jira connect started',
                                            notificationKey:
                                                'jira-connect-started',
                                        })
                                    }
                                    if (
                                        status?.connected &&
                                        !status?.tokenExpired
                                    ) {
                                        showActionNotification({
                                            type: 'success',
                                            message:
                                                'Jira is already connected',
                                            subMessage:
                                                status?.siteName ||
                                                status?.siteUrl,
                                            notificationKey:
                                                'jira-already-connected',
                                        })
                                    } else if (
                                        status?.connected &&
                                        status?.tokenExpired
                                    ) {
                                        showActionNotification({
                                            type: 'warning',
                                            message: 'Jira token expired',
                                            subMessage:
                                                'Reconnect to continue syncing.',
                                            notificationKey:
                                                'jira-token-expired',
                                        })
                                        window.open(
                                            `${getBackendBaseUrl()}/support/jira/oauth/connect`,
                                            '_blank',
                                            'noopener,noreferrer'
                                        )
                                    } else if (data?.success === false) {
                                        showActionNotification({
                                            type: 'error',
                                            message:
                                                data?.message ||
                                                'Could not start Jira connect',
                                            notificationKey:
                                                'jira-connect-failed',
                                        })
                                    } else {
                                        // Fallback: open the endpoint directly (handles 302 redirects)
                                        window.open(
                                            `${getBackendBaseUrl()}/support/jira/oauth/connect`,
                                            '_blank',
                                            'noopener,noreferrer'
                                        )
                                    }
                                } catch {
                                    showActionNotification({
                                        type: 'error',
                                        message: 'Could not start Jira connect',
                                        notificationKey: 'jira-connect-failed',
                                    })
                                } finally {
                                    setJiraConnecting(false)
                                }
                            }}
                        >
                            {userProfile?.jiraConnected
                                ? 'Jira Connected'
                                : 'Connect Jira'}
                        </ButtonAnt>
                    )}
                <HeaderNotifications />
                <div className={headerStyles.headerCenterSearch}>
                    <div className={headerStyles.headerSearchWrapper}>
                        <Input
                            className={headerStyles.headerSearchInput}
                            placeholder="Search softwareco.com"
                            prefix={<SearchIconWhite />}
                            variant="borderless"
                            readOnly
                            onClick={() => setPaletteOpen(true)}
                        />
                    </div>
                </div>
            </div>

            {paletteOpen ? (
                <div
                    className={headerStyles.searchPaletteOverlay}
                    role="presentation"
                    onMouseDown={(e) => {
                        // click outside to close
                        if (e.target === e.currentTarget) {
                            setPaletteOpen(false)
                        }
                    }}
                >
                    <div
                        className={headerStyles.searchPalettePanel}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Search"
                    >
                        <div className={headerStyles.searchPaletteTop}>
                            <div
                                className={`${headerStyles.headerSearchWrapper} ${headerStyles.searchPaletteHeaderSearchWrapper}`}
                            >
                                <Input
                                    ref={paletteInputRef}
                                    value={paletteQuery}
                                    onChange={(e) =>
                                        setPaletteQuery(e.target.value)
                                    }
                                    placeholder="Search softwareco.com"
                                    variant="borderless"
                                    className={`${headerStyles.headerSearchInput} ${headerStyles.searchPaletteHeaderSearchInput}`}
                                />
                            </div>
                        </div>

                        <div className={headerStyles.searchPaletteTabs}>
                            {categoryTabs.map((t) => (
                                <button
                                    key={t.key}
                                    type="button"
                                    className={`${headerStyles.searchPaletteTab}${activeTab === t.key ? ` ${headerStyles.searchPaletteTabActive}` : ''}`}
                                    onClick={() =>
                                        setActiveTab(
                                            activeTab === t.key ? null : t.key
                                        )
                                    }
                                >
                                    <Icon
                                        icon={t.icon}
                                        width={15}
                                        height={15}
                                    />
                                    <span
                                        className={
                                            headerStyles.searchPaletteTabLabel
                                        }
                                    >
                                        {t.label}
                                    </span>
                                    {activeTab === t.key ? (
                                        <Icon
                                            icon="mdi:close"
                                            width={12}
                                            height={12}
                                            className={
                                                headerStyles.searchPaletteTabClose
                                            }
                                        />
                                    ) : null}
                                </button>
                            ))}
                        </div>

                        <div
                            ref={searchResultsScrollRef}
                            className={headerStyles.searchPaletteResults}
                        >
                            {globalSearchLoading &&
                            filteredGlobalSearchSections.length === 0 ? (
                                <div className={headerStyles.searchPaletteList}>
                                    <div
                                        className={
                                            headerStyles.searchPaletteSkeletonInline
                                        }
                                    >
                                        {[56, 30, 20, 42, 24, 16].map(
                                            (w, idx) => (
                                                <Skeleton.Input
                                                    key={`search-skeleton-${idx}`}
                                                    active
                                                    size="small"
                                                    style={{ width: `${w}%` }}
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            ) : filteredGlobalSearchSections.length > 0 ? (
                                filteredGlobalSearchSections.map((section) => (
                                    <div
                                        className={
                                            headerStyles.searchPaletteSection
                                        }
                                        key={section.key}
                                    >
                                        <span
                                            className={`${headerStyles.searchPaletteSectionLabel} ${headerStyles.searchPaletteSectionLabelCompact}`}
                                        >
                                            {section.title} ({section.total})
                                        </span>
                                        <div
                                            className={
                                                headerStyles.searchPaletteList
                                            }
                                        >
                                            {section.items.map((item) => {
                                                const titleKey = String(
                                                    section?.title || ''
                                                )
                                                    .toLowerCase()
                                                    .trim()
                                                const normalizedSectionKey =
                                                    sectionKeyByTitle[titleKey]
                                                const useTaskNav =
                                                    normalizedSectionKey ===
                                                        'task' ||
                                                    normalizedSectionKey ===
                                                        'supportTask' ||
                                                    (normalizedSectionKey ==
                                                        null &&
                                                        isSearchTaskRowItem(
                                                            item
                                                        ))
                                                const taskKindForNav =
                                                    normalizedSectionKey ===
                                                    'task'
                                                        ? 'task'
                                                        : normalizedSectionKey ===
                                                            'supportTask'
                                                          ? 'supportTask'
                                                          : 'unknown'
                                                return renderPaletteRow(
                                                    item,
                                                    section.title?.toLowerCase(),
                                                    normalizedSectionKey ===
                                                        'project'
                                                        ? () => {
                                                              navigate(
                                                                  `/dashboard/projects/${item?._id}`
                                                              )
                                                              setPaletteOpen(
                                                                  false
                                                              )
                                                          }
                                                        : useTaskNav
                                                          ? () =>
                                                                navigateToTaskFromSearch(
                                                                    item,
                                                                    taskKindForNav
                                                                )
                                                          : undefined
                                                )
                                            })}
                                            <SearchPaletteInfiniteSentinel
                                                enabled={Boolean(section.isNext)}
                                                loading={
                                                    loadingMoreSectionKey ===
                                                    (sectionKeyByTitle[
                                                        String(
                                                            section?.title || ''
                                                        ).toLowerCase()
                                                    ] || null)
                                                }
                                                scrollRootRef={
                                                    searchResultsScrollRef
                                                }
                                                onLoadMore={() =>
                                                    handleLoadMoreSection(
                                                        section.title
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                ))
                            ) : paletteQuery?.trim() || activeTab ? (
                                <div
                                    className={
                                        headerStyles.searchPaletteEmptyState
                                    }
                                >
                                    <span
                                        className={
                                            headerStyles.searchPaletteEmptyText
                                        }
                                    >
                                        No results found
                                    </span>
                                </div>
                            ) : (
                                <>
                                    {recentFetchStatus === 'loading' ? (
                                        <div
                                            className={
                                                headerStyles.searchPaletteEmptyState
                                            }
                                        >
                                            <span
                                                className={
                                                    headerStyles.searchPaletteEmptyText
                                                }
                                            >
                                                Loading recents...
                                            </span>
                                        </div>
                                    ) : recentItems.length > 0 ? (
                                        <div
                                            className={
                                                headerStyles.searchPaletteSection
                                            }
                                        >
                                            <span
                                                className={
                                                    headerStyles.searchPaletteSectionLabel
                                                }
                                            >
                                                Recents
                                            </span>
                                            <div
                                                className={
                                                    headerStyles.searchPaletteList
                                                }
                                            >
                                                {recentItems.map((item) =>
                                                    renderPaletteRow(
                                                        item,
                                                        item.type,
                                                        item.type === 'project'
                                                            ? () => {
                                                                  navigate(
                                                                      `/dashboard/projects/${item?._id}`
                                                                  )
                                                                  setPaletteOpen(
                                                                      false
                                                                  )
                                                              }
                                                            : item.type ===
                                                                    'task' ||
                                                                item.type ===
                                                                    'supportTask' ||
                                                                isSearchTaskRowItem(
                                                                    item
                                                                )
                                                              ? () =>
                                                                    navigateToTaskFromSearch(
                                                                        item,
                                                                        item.type ===
                                                                            'task'
                                                                            ? 'task'
                                                                            : item.type ===
                                                                                'supportTask'
                                                                              ? 'supportTask'
                                                                              : 'unknown'
                                                                    )
                                                              : undefined
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    ) : null}
                                    {/* <div
                                        className={
                                            headerStyles.searchPaletteSection
                                        }
                                    >
                                        <span
                                            className={`${headerStyles.searchPaletteSectionLabel} ${headerStyles.searchPaletteSectionLabelCompact}`}
                                        >
                                            Saved searches
                                        </span>
                                        <div
                                            className={
                                                headerStyles.searchPaletteChips
                                            }
                                        >
                                            {SAVED_SEARCHES.map((s) => (
                                                <button
                                                    key={s.key}
                                                    type="button"
                                                    className={
                                                        headerStyles.searchPaletteChip
                                                    }
                                                >
                                                    <Icon
                                                        icon={s.icon}
                                                        width={13}
                                                        height={13}
                                                    />
                                                    <span>{s.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div> */}
                                </>
                            )}
                        </div>
                        {paletteQuery?.trim() && (
                            <div className={headerStyles.searchPaletteFooter}>
                                <div
                                    className={
                                        headerStyles.searchPaletteFooterRow
                                    }
                                    role="button"
                                    tabIndex={0}
                                >
                                    <Icon
                                        icon="mdi:magnify"
                                        width={16}
                                        height={16}
                                    />
                                    <span>View all results</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </>
    )
}

export default HeaderRightSection
